import type {ReactNode} from 'react';
import {useCallback} from 'react';
import OtpInput from 'react-otp-input';

import OtpVerifyImg from '@/assets/svg/otp-verify.svg';
import {SpinningLoader} from '@/components';
import {useDarkMode} from '@/context/ThemeProviderContext';

import type useOTP from './useOTP';

// `Auth/OTP/index.tsx` forwards `useOTP()` straight through, so the hook's
// return type is the prop contract. Mirrors `LoginFormProps` in `useLogin.ts`.
export type OTPFormProps = ReturnType<typeof useOTP>;

const OTPForm = ({
  isLoading,
  otp,
  handleOTPChange,
  handleResendOTP,
  countdown,
}: OTPFormProps) => {
  const {isDark} = useDarkMode();
  const email = localStorage.getItem('logging-in');
  const renderButton = useCallback((): ReactNode => {
    let button: React.ReactNode;
    if (countdown === null) {
      button = (
        <button className="text-sm font-semibold" onClick={handleResendOTP}>
          Send a new code
        </button>
      );
    } else if (countdown > 0) {
      const minutes = Math.floor(countdown / 60);
      const seconds = countdown % 60;
      const timeStr = `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
      button = (
        <button className="text-sm font-semibold" disabled>
          {timeStr}
        </button>
      );
    }
    if (countdown === 0) {
      button = (
        <button className="text-sm font-semibold" onClick={handleResendOTP}>
          Resend code
        </button>
      );
    }
    return button;
  }, [countdown, handleResendOTP]);

  return (
    <div className="flex justify-center items-center min-h-screen px-4 dark:bg-dark-background">
      {isLoading ? (
        <div className="flex w-full h-full justify-center items-center my-5">
          <SpinningLoader size={14} />
        </div>
      ) : (
        <div className="flex flex-col w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[500px] items-center justify-center border rounded-md pt-10 px-4 sm:px-7 pb-7 gap-2 dark:border-dark-navBorder">
          <h2
            className="font-medium text-xl dark:text-gray-200"
            data-cy="admin-otp-heading"
          >
            Verification required
          </h2>

          <h3 className="text-center text-md dark:text-gray-400">
            Enter the code sent to{' '}
            <span className="text-cyan-600">{email}</span>
          </h3>

          <img src={OtpVerifyImg} alt="loading..." className="w-20 h-24 mt-5" />

          <OtpInput
            containerStyle="mt-10"
            value={otp}
            onChange={handleOTPChange}
            numInputs={6}
            renderSeparator={<span style={{width: '8px'}}></span>}
            shouldAutoFocus={true}
            inputStyle={{
              borderColor: isDark ? '#4B5563' : 'gray',
              backgroundColor: isDark ? '#1F2937' : '#fff',
              borderRadius: '8px',
              width: '40px',
              height: '48px',
              fontSize: '16px',
              color: isDark ? '#F3F4F6' : '#475467',
              fontWeight: '400',
              caretColor: isDark ? '#3B82F6' : 'blue',
            }}
            inputType="tel"
            renderInput={(props) => (
              <input
                {...props}
                inputMode="numeric"
                pattern="[0-9]*"
                data-cy="admin-otp-input"
              />
            )}
          />

          <h3 className="font-sans mt-5 font-bold text-l text-purple-500">
            {renderButton()}
          </h3>

          <div className="flex flex-col items-center justify-center w-full sm:w-4/5 mt-5">
            <div className="border-b border-slate-200 w-full dark:border-dark-navBorder" />
            <div className="w-full sm:w-[320px] mt-5 bg-purpleBg-500 dark:bg-gray-800 rounded-xl text-center p-3">
              <p className="text-gray-700 dark:text-gray-100 text-xs leading-5">
                If you're unable to locate your verification code, please ensure
                to look through your spam folder
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OTPForm;
